import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Types
interface Stakeholder {
  id: string;
  name: string;
  relationship?: string;
  influence_level?: number;
  interest_level?: number;
  [key: string]: any;
}

interface QuadrantGridViewProps {
  stakeholders: Stakeholder[];
  showLabels?: boolean;
  width?: number;
  height?: number;
}

// Helper functions
const band = (raw: any): 'high' | 'medium' | 'low' => {
  if (raw == null) return 'medium';
  const n = Number(raw);
  if (!Number.isNaN(n)) {
    if (n >= 5) return 'high';
    if (n >= 3) return 'medium';
    return 'low';
  }
  const s = String(raw).toLowerCase();
  if (s.startsWith('high')) return 'high';
  if (s.startsWith('low')) return 'low';
  return 'medium';
};

const canonRelWide = (raw: any): 'supportive' | 'neutral' | 'opposed' | 'unknown' => {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (['supportive', 'support', 'ally', 'advocate', 'supporter', 'for', 'pro', 'positive', 'favorable', 'favourable', 'backing'].includes(s)) 
    return 'supportive';
  if (['neutral', 'mixed', 'ambivalent'].includes(s)) 
    return 'neutral';
  if (['opposed', 'oppose', 'opponent', 'against', 'anti', 'adversarial', 'hostile', 'negative', 'unfavorable', 'unfavourable', 'blocking'].includes(s)) 
    return 'opposed';
  const n = Number(s);
  if (Number.isFinite(n)) {
    if (n <= 1) return 'opposed';
    if (n === 2) return 'neutral';
    if (n >= 3) return 'supportive';
  }
  return 'unknown';
};

const relationshipColor = (rel: string): string => {
  const colors = {
    supportive: '#1E8E3E',
    neutral: '#9AA0A6',
    opposed: '#D93025',
    unknown: '#9CA3AF'
  };
  return colors[canonRelWide(rel) as keyof typeof colors] || colors.unknown;
};

const sizeByBand = (b: string): number => (b === 'high' ? 40 : b === 'low' ? 20 : 30);

const QuadrantGridView: React.FC<QuadrantGridViewProps> = ({
  stakeholders,
  showLabels = true,
  width = 1000,
  height = 700,
}) => {
  const navigate = useNavigate();
  const [hideDenseLabels, setHideDenseLabels] = useState(true);
  const [denseThreshold, setDenseThreshold] = useState(8);
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  const padding = 80;
  const innerWidth = width - padding * 2;
  const innerHeight = height - padding * 2;

  // Position helpers
  const xForRelationship = (relationship: string): number => {
    switch (canonRelWide(relationship)) {
      case 'supportive': return padding + innerWidth * 0.65;
      case 'neutral': return padding + innerWidth * 0.33;
      case 'opposed': return padding + innerWidth * 0.10;
      default: return padding + innerWidth * 0.85; // unknown
    }
  };

  const yForInfluence = (influenceLevel: any): number => {
    const b = band(influenceLevel);
    if (b === 'high') return padding;
    if (b === 'low') return padding + innerHeight * 0.66;
    return padding + innerHeight * 0.33;
  };

  // Compute positioned nodes
  const positioned = useMemo(() => {
    const buckets = new Map<string, Stakeholder[]>();
    
    for (const s of stakeholders) {
      const rb = canonRelWide(s.relationship);
      const ib = band(s.influence_level);
      const key = `${rb}|${ib}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(s);
    }

    const maxCols = 6;
    const cellSpacing = 60;
    const result: Array<Stakeholder & { x: number; y: number; isDense: boolean }> = [];

    buckets.forEach((items, key) => {
      const [rb, ib] = key.split('|');
      const baseX = xForRelationship(rb);
      const baseY = yForInfluence(ib === 'high' ? 5 : ib === 'low' ? 1 : 3);
      const isDense = items.length > denseThreshold;
      const cols = Math.min(maxCols, Math.ceil(Math.sqrt(items.length)));
      const rows = Math.ceil(items.length / cols);

      items.forEach((item, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const offsetX = (col - (cols - 1) / 2) * cellSpacing;
        const offsetY = (row - (rows - 1) / 2) * cellSpacing;
        
        result.push({
          ...item,
          x: baseX + offsetX,
          y: baseY + offsetY,
          isDense
        });
      });
    });

    return result;
  }, [stakeholders, denseThreshold, innerWidth, innerHeight, padding]);

  return (
    <div className="quadrant-grid-container">
      <div className="grid-controls" style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={hideDenseLabels}
            onChange={(e) => setHideDenseLabels(e.target.checked)}
          />
          Hide labels in dense buckets
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Threshold:
          <input
            type="number"
            value={denseThreshold}
            onChange={(e) => setDenseThreshold(Math.max(1, parseInt(e.target.value) || 8))}
            style={{ width: '60px', padding: '4px' }}
            min="1"
          />
        </label>
      </div>

      <svg width={width} height={height} style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
        {/* Grid lines */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#d1d5db" strokeWidth="2" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#d1d5db" strokeWidth="2" />

        {/* Axis labels */}
        <text x={width / 2} y={height - 20} textAnchor="middle" fontSize="14" fill="#6b7280" fontWeight="600">
          Client Relationship
        </text>
        <text x={20} y={height / 2} textAnchor="middle" fontSize="14" fill="#6b7280" fontWeight="600" transform={`rotate(-90, 20, ${height / 2})`}>
          Influence Level
        </text>

        {/* Quadrant labels */}
        <text x={padding + innerWidth * 0.10} y={height - padding + 30} textAnchor="middle" fontSize="12" fill="#6b7280">
          Opposed
        </text>
        <text x={padding + innerWidth * 0.33} y={height - padding + 30} textAnchor="middle" fontSize="12" fill="#6b7280">
          Neutral
        </text>
        <text x={padding + innerWidth * 0.65} y={height - padding + 30} textAnchor="middle" fontSize="12" fill="#6b7280">
          Supportive
        </text>
        <text x={padding + innerWidth * 0.85} y={height - padding + 30} textAnchor="middle" fontSize="12" fill="#6b7280">
          Unknown
        </text>

        <text x={padding - 40} y={padding} textAnchor="middle" fontSize="12" fill="#6b7280">
          High
        </text>
        <text x={padding - 40} y={padding + innerHeight * 0.33} textAnchor="middle" fontSize="12" fill="#6b7280">
          Medium
        </text>
        <text x={padding - 40} y={padding + innerHeight * 0.66} textAnchor="middle" fontSize="12" fill="#6b7280">
          Low
        </text>

        {/* Stakeholder nodes */}
        {positioned.map((node) => {
          const size = sizeByBand(band(node.influence_level));
          const color = relationshipColor(node.relationship || '');
          const showLabel = showLabels && (!hideDenseLabels || !node.isDense || hoverKey === node.id);

          return (
            <g key={node.id}>
              <circle
                cx={node.x}
                cy={node.y}
                r={size / 2}
                fill={color}
                stroke={hoverKey === node.id ? '#000' : '#fff'}
                strokeWidth={hoverKey === node.id ? 2 : 1}
                opacity={0.8}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/stakeholders/${node.id}`)}
                onMouseEnter={() => setHoverKey(node.id)}
                onMouseLeave={() => setHoverKey(null)}
              >
                <title>{`${node.name}\nRelationship: ${canonRelWide(node.relationship)}\nInfluence: ${band(node.influence_level)}`}</title>
              </circle>
              {showLabel && (
                <text
                  x={node.x}
                  y={node.y + size / 2 + 14}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#374151"
                  fontWeight={hoverKey === node.id ? '600' : '400'}
                  pointerEvents="none"
                >
                  {node.name.length > 20 ? node.name.substring(0, 18) + '...' : node.name}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default QuadrantGridView;
